import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import TourOutlinedIcon from '@mui/icons-material/TourOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import { anchorIndex, HELP_PANEL_ATTR, stepAtElement, type AnchoredStep } from './dom';
import { cardPosition, rectOf, type Rect } from './position';
import { uiText } from './text';
import type { HelpLanguage, HelpStep, PageHelp } from './types';
import { localizedSection, localizedStep } from './types';

/**
 * Click-to-explain — "what is THIS field?", answered by pointing at it.
 *
 * A user looking at a control they do not understand already knows where it is on screen; asking
 * them to find it in a list first is asking the wrong question. Here the screen itself is the index.
 *
 * Three things make it safe to run over a half-filled form:
 *
 *  - **Every page interaction is swallowed** — pointer, mouse and click events are claimed in the
 *    CAPTURE phase at the window, so a click that asks "what is this?" cannot also save a cell,
 *    open a select or move focus out of a field mid-edit.
 *  - **Nothing is dimmed.** A scrim would hide the form being read. The mode announces itself with
 *    a banner, a cursor and an outline instead.
 *  - **The guide's own surfaces are exempt** (`data-help-panel`), so this card's buttons keep working.
 */

const CARD_WIDTH = 330;
const CARD_HEIGHT = 190;
/** How long "nothing documented here" stays up before it stops being an answer and starts nagging. */
const MISS_MS = 2200;

const HOVER_COLOR = '#F39200';
const PINNED_COLOR = '#235592';

/** Outline drawn over a control. Never interactive — the real element has to stay hit-testable. */
const Highlight = ({ rect, color, dashed }: { rect: Rect; color: string; dashed?: boolean }): JSX.Element => (
  <Box
    aria-hidden
    sx={{
      position: 'fixed',
      top: rect.top - 3,
      left: rect.left - 3,
      width: rect.width + 6,
      height: rect.height + 6,
      borderRadius: 1,
      border: `2px ${dashed ? 'dashed' : 'solid'} ${color}`,
      bgcolor: dashed ? 'transparent' : 'rgba(35, 85, 146, 0.06)',
      pointerEvents: 'none',
      zIndex: (t) => t.zIndex.modal + 20,
      transition: 'top 80ms ease, left 80ms ease, width 80ms ease, height 80ms ease',
    }}
  />
);

/** The events of a press, all of which have to be swallowed: a select opens on `mousedown`. */
const PRESS_EVENTS = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'dblclick', 'contextmenu'];

interface HelpInspectProps {
  /** Role-filtered steps for the current page — the same list the tour and drawer are given. */
  steps: HelpStep[];
  /** The guide — for the translated section names. */
  help: PageHelp;
  /** `PageHelp.contexts` in the reader's language, so a pinned step can say how it is reached. */
  contexts?: Record<string, string>;
  language: HelpLanguage;
  /** Continue as a normal walk from the field just explained. */
  onTourFrom: (step: HelpStep) => void;
  onExit: () => void;
}

export const HelpInspect = ({
  steps,
  help,
  contexts,
  language,
  onTourFrom,
  onExit,
}: HelpInspectProps): JSX.Element => {
  const index = useMemo(() => anchorIndex(steps), [steps]);
  const text = uiText(language);

  const [hover, setHover] = useState<AnchoredStep | null>(null);
  const [hoverRect, setHoverRect] = useState<Rect | null>(null);
  const [pinned, setPinned] = useState<AnchoredStep | null>(null);
  const [pinnedRect, setPinnedRect] = useState<Rect | null>(null);
  const [miss, setMiss] = useState<{ top: number; left: number } | null>(null);

  /** The element the pointer last resolved to, so a mouse move over the SAME control is free. */
  const hoveredEl = useRef<HTMLElement | null>(null);
  const missTimer = useRef(0);
  /**
   * The exit callback, read through a ref: the listeners below include `pointermove`, and
   * re-attaching that set whenever the parent re-renders is churn on the hottest event there is.
   */
  const exitRef = useRef(onExit);
  useEffect(() => {
    exitRef.current = onExit;
  }, [onExit]);

  const remeasure = useCallback((): void => {
    if (hoveredEl.current) setHoverRect(rectOf(hoveredEl.current));
    setPinnedRect((current) => (pinned && current ? rectOf(pinned.element) : current));
  }, [pinned]);

  // Split from the interaction listeners: this pair depends on what is pinned, and the pointer
  // listeners must not be torn down every time something is.
  useEffect(() => {
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
  }, [remeasure]);

  useEffect(() => {
    const own = (target: EventTarget | null): boolean =>
      target instanceof Element && target.closest(`[${HELP_PANEL_ATTR}]`) !== null;

    const onMove = (event: PointerEvent): void => {
      const found = stepAtElement(event.target, index);
      if (found?.element === hoveredEl.current) return;
      hoveredEl.current = found?.element ?? null;
      setHover(found);
      setHoverRect(found ? rectOf(found.element) : null);
    };

    /** The page is inert while the mode is on — the whole press sequence, not just `click`. */
    const swallow = (event: Event): void => {
      if (own(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const onClick = (event: MouseEvent): void => {
      if (own(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      const found = stepAtElement(event.target, index);
      if (!found) {
        // Said out loud rather than ignored: a click that does nothing reads as the mode being
        // broken, when the truth is that this control is not documented yet.
        window.clearTimeout(missTimer.current);
        setMiss({ top: event.clientY, left: event.clientX });
        missTimer.current = window.setTimeout(() => setMiss(null), MISS_MS);
        return;
      }
      setMiss(null);
      setPinned(found);
      setPinnedRect(rectOf(found.element));
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      // Capture + stop, like the tour: leaving must not also dismiss the dialog underneath.
      event.stopPropagation();
      event.preventDefault();
      exitRef.current();
    };

    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKey, true);
    for (const name of PRESS_EVENTS) window.addEventListener(name, swallow, true);

    const timer = missTimer;
    return () => {
      window.clearTimeout(timer.current);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKey, true);
      for (const name of PRESS_EVENTS) window.removeEventListener(name, swallow, true);
    };
  }, [index]);

  const card = pinnedRect ? cardPosition(pinnedRect, { width: CARD_WIDTH, height: CARD_HEIGHT }) : null;
  const contextHint = pinned?.step.context ? contexts?.[pinned.step.context] : undefined;
  const showHover = hoverRect !== null && hover?.element !== pinned?.element;
  const pinnedText = pinned ? localizedStep(pinned.step, language) : null;

  return (
    <Portal>
      {/* A field's own `text` cursor would win against anything set on the body — hence the
          sweep, with the guide's own surfaces put back the way they were. */}
      <GlobalStyles
        styles={{
          'body *': { cursor: 'help !important' },
          '[data-help-panel] *': { cursor: 'auto !important' },
          '[data-help-panel] button, [data-help-panel] button *': { cursor: 'pointer !important' },
        }}
      />

      {showHover && hoverRect && <Highlight rect={hoverRect} color={HOVER_COLOR} dashed />}
      {pinnedRect && <Highlight rect={pinnedRect} color={PINNED_COLOR} />}

      <Stack
        direction="row"
        spacing={1}
        {...{ [HELP_PANEL_ATTR]: 'inspect-banner' }}
        sx={{
          position: 'fixed',
          top: 14,
          left: '50%',
          transform: 'translateX(-50%)',
          alignItems: 'center',
          zIndex: (t) => t.zIndex.modal + 22,
          bgcolor: '#1F2F45',
          color: 'common.white',
          borderRadius: 999,
          pl: 2,
          pr: 0.75,
          py: 0.5,
          boxShadow: '0 8px 24px rgba(31, 47, 69, 0.35)',
          maxWidth: 'calc(100vw - 32px)',
        }}
      >
        <TouchAppOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600 }}>
          {hover ? localizedStep(hover.step, language).title : text.inspectBanner}
        </Typography>
        <Typography sx={{ fontSize: 11, opacity: 0.65, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
          {text.inspectEsc}
        </Typography>
        <IconButton
          size="small"
          onClick={onExit}
          aria-label={text.inspectLeaveAria}
          sx={{ color: 'common.white', flexShrink: 0 }}
        >
          <CloseOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      {miss && (
        <Box
          aria-live="polite"
          {...{ [HELP_PANEL_ATTR]: 'inspect-miss' }}
          sx={{
            position: 'fixed',
            top: Math.min(miss.top + 14, window.innerHeight - 60),
            left: Math.min(miss.left + 14, window.innerWidth - 260),
            width: 240,
            zIndex: (t) => t.zIndex.modal + 22,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 1.5,
            py: 1,
            boxShadow: '0 6px 18px rgba(31, 47, 69, 0.22)',
            pointerEvents: 'none',
          }}
        >
          <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
            {text.inspectMiss}
          </Typography>
        </Box>
      )}

      {pinned && pinnedText && card && (
        <Box
          role="dialog"
          {...{ [HELP_PANEL_ATTR]: 'inspect' }}
          aria-label={text.inspectCardAria(pinnedText.title)}
          sx={{
            position: 'fixed',
            top: card.top,
            left: card.left,
            width: CARD_WIDTH,
            zIndex: (t) => t.zIndex.modal + 21,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 12px 32px rgba(31, 47, 69, 0.28)',
            p: 2,
          }}
        >
          {pinned.step.section && (
            <Typography
              noWrap
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: 'text.disabled',
              }}
            >
              {localizedSection(help, pinned.step.section, language)}
            </Typography>
          )}
          <Typography sx={{ fontSize: 14.5, fontWeight: 600, mt: 0.25 }}>{pinnedText.title}</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, lineHeight: 1.55 }}>
            {pinnedText.body}
          </Typography>
          {contextHint && (
            <Typography
              sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.75, fontStyle: 'italic', lineHeight: 1.5 }}
            >
              {contextHint}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 1.75, alignItems: 'center' }}>
            <Button
              size="small"
              color="inherit"
              startIcon={<TourOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => onTourFrom(pinned.step)}
              data-help="help-inspect-tour"
              sx={{ fontSize: 11.5, color: 'text.secondary', ml: -0.5 }}
            >
              {text.continueFromHere}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" onClick={onExit}>
              {text.done}
            </Button>
          </Stack>
        </Box>
      )}
    </Portal>
  );
};
